import { useState, useMemo, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { Eye, EyeOff, Check, X } from 'lucide-react';

interface PasswordRule {
  label: string;
  test: (pw: string) => boolean;
}

const rules: PasswordRule[] = [
  { label: '8+ chars', test: (pw) => pw.length >= 8 },
  { label: 'Uppercase', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'Lowercase', test: (pw) => /[a-z]/.test(pw) },
  { label: 'Number', test: (pw) => /\d/.test(pw) },
];

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const ruleResults = useMemo(
    () => rules.map((r) => ({ ...r, passed: r.test(password) })),
    [password],
  );
  const allPassed = ruleResults.every((r) => r.passed);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!allPassed || !acceptTerms) return;
    setError('');
    setLoading(true);

    try {
      await signup({ email, password });
      const checkEmailUrl = redirect ? `/check-email?redirect=${encodeURIComponent(redirect)}` : '/check-email';
      navigate(checkEmailUrl, { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Signup failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h1 className="text-lg font-extrabold mb-1">Create Account</h1>
      <p className="text-xs text-ink-3 mb-1.5">Build your first AI trading agent — free</p>

      {/* Gradient divider */}
      <div
        className="h-px mb-6"
        style={{ background: 'linear-gradient(to right, transparent, rgba(249,115,22,0.2), transparent)' }}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">Email</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 7l-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full pl-9 pr-3 py-2.5 text-sm font-mono rounded-lg bg-surface text-ink placeholder:text-ink-4 outline-none transition-all duration-200 border border-border focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">Password</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full pl-9 pr-10 py-2.5 text-sm font-mono rounded-lg bg-surface text-ink placeholder:text-ink-4 outline-none transition-all duration-200 border border-border focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-4 hover:text-ink-3 transition-colors"
            >
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        {/* Password rules */}
        <div className="flex flex-wrap gap-2">
          {ruleResults.map((r) => (
            <span
              key={r.label}
              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md transition-colors ${
                r.passed ? 'bg-green-bg text-green' : 'bg-surface text-ink-4'
              }`}
            >
              {r.passed ? <Check size={10} /> : <X size={10} />}
              {r.label}
            </span>
          ))}
        </div>

        {/* Referral code */}
        <div>
          <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">
            Referral Code <span className="text-ink-4 font-normal">(optional)</span>
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Enter referral code"
              className="w-full pl-9 pr-3 py-2.5 text-sm font-mono rounded-lg bg-surface text-ink placeholder:text-ink-4 outline-none transition-all duration-200 border border-border focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
            />
          </div>
        </div>

        {/* Terms & conditions */}
        <label className="flex items-start gap-2 cursor-pointer group">
          <div
            className="w-4 h-4 rounded flex items-center justify-center transition-all duration-200 mt-0.5 shrink-0"
            style={{
              background: acceptTerms ? 'linear-gradient(135deg, #f97316, #f59e0b)' : 'var(--color-surface)',
              border: acceptTerms ? '1px solid rgba(249,115,22,0.4)' : '1px solid var(--color-border-2)',
            }}
            onClick={() => setAcceptTerms((v) => !v)}
          >
            {acceptTerms && <Check size={10} className="text-white" />}
          </div>
          <span className="text-[11px] text-ink-3 group-hover:text-ink-2 transition-colors select-none leading-relaxed">
            I agree to the{' '}
            <a href={`${import.meta.env.VITE_SITE_URL || 'http://localhost:5174'}/terms`} target="_blank" rel="noopener noreferrer" className="text-orange font-semibold hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href={`${import.meta.env.VITE_SITE_URL || 'http://localhost:5174'}/privacy`} target="_blank" rel="noopener noreferrer" className="text-orange font-semibold hover:underline">Privacy Policy</a>
          </span>
        </label>

        {error && (
          <p className="text-xs text-red font-semibold">{error}</p>
        )}

        {/* CTA */}
        <button
          type="submit"
          disabled={loading || !allPassed || !acceptTerms}
          className="w-full py-3 rounded-lg text-sm font-bold text-white transition-all hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{
            background: 'linear-gradient(135deg, #f97316, #f59e0b)',
            boxShadow: '0 0 20px rgba(249,115,22,0.25)',
          }}
        >
          {loading ? 'Creating account...' : 'Create Account'}
          {!loading && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          )}
        </button>
      </form>

      {/* Trust note */}
      <p className="text-[10px] text-ink-4 text-center mt-3">Free to start — no credit card required</p>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
        <span className="text-[10px] text-ink-4 font-medium">or continue with</span>
        <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
      </div>

      {/* Social buttons */}
      <a
        href={`${import.meta.env.VITE_API_URL || '/api'}/auth/google`}
        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-xs font-semibold text-ink-3 transition-all hover:bg-glass-2 hover:text-ink"
        style={{ background: 'var(--color-glass)', border: '1px solid var(--color-glass-border)' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </a>

      {/* Toggle link */}
      <p className="text-xs text-ink-3 text-center mt-6">
        Already have an account?{' '}
        <Link to={redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login'} className="text-orange font-semibold hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
